<template>
<div class="distorted-button">
  <svg xmlns="http://www.w3.org/2000/svg" version="1.1" class="svg-filters">
    <defs>
      <filter :id="filterId" ref="filter">
        <feGaussianBlur in="SourceGraphic" stdDeviation="7" result="blur" />
        <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 19 -9" result="goo" />
        <feComposite in="SourceGraphic" in2="goo" />
      </filter>
    </defs>
  </svg>
  <button ref="button" v-on="$listeners" v-bind="$attrs">
    <slot></slot>
  </button>
  <span class="button__container" ref="buttonContainer">
    <span class="circle top-left" ref="circlesTopLeft" v-for="i in [1, 2, 3]" :key="`circle-top-left-${i}`"></span>
    <span class="button__bg"></span>
    <span class="circle bottom-right" ref="circlesBottomRight" v-for="i in [1, 2, 3]" :key="`circle-bottom-right-${i}`"></span>
  </span>
</div>
</template>

<script>
// Copy-pasted from https://github.com/codrops/DistortedButtonEffects
export default {
  inheritAttrs: false,

  props: {
    filterId: {
      type: String,
      default: () => `filter-goo-${Math.random().toString()}`,
    },
  },

  mounted() {
    this.makeAnimated();
  },
  beforeDestroy() {
    this.beingDestroyed = true;
  },

  methods: {
    async makeAnimated() {
      const coreP = import(/* webpackPreload: true */ 'gsap');
      const easeP = import(/* webpackPreload: true */ 'gsap/EasePack');
      const { TimelineLite, gsap } = await coreP;
      const { SlowMo } = await easeP;

      if (this.beingDestroyed) return;

      gsap.registerPlugin(TimelineLite);
      gsap.registerEase(SlowMo);

      const bt1 = this.$refs.button;
      const bt1c = this.$refs.buttonContainer;
      const $circlesTopLeft = this.$refs.circlesTopLeft;
      const $circlesBottomRight = this.$refs.circlesBottomRight;

      const filter = this.$refs.filter;

      const tl = new TimelineLite();
      const tl2 = new TimelineLite();

      const btTl = new TimelineLite({
        paused: true,
        onUpdate: function() {
          filter.setAttribute('x', 0);
        },
        onComplete: function() {
          bt1c.style.filter = 'none';
        }
      });

      const prePopDurationMultiplier = 0.5;
      const pm = prePopDurationMultiplier;

      tl.to($circlesTopLeft, 1.2, { x: -25, y: -25, scaleY: 2, ease: SlowMo.ease.config(0.1, 0.7, false) });
      tl.to($circlesTopLeft[0], 0.1, { scale: 0.2, x: '+=6', y: '-=2' });
      tl.to($circlesTopLeft[1], 0.1, { scaleX: 1, scaleY: 0.8, x: '-=10', y: '-=7' }, '-=0.1');
      tl.to($circlesTopLeft[2], 0.1, { scale: 0.2, x: '-=15', y: '+=6' }, '-=0.1');
      tl.to($circlesTopLeft[0], 1, { scale: 0, x: '-=5', y: '-=15', opacity: 0 });
      tl.to($circlesTopLeft[1], 1, { scaleX: 0.4, scaleY: 0.4, x: '-=10', y: '-=10', opacity: 0 }, '-=1');
      tl.to($circlesTopLeft[2], 1, { scale: 0, x: '-=15', y: '+=5', opacity: 0 }, '-=1');

      const tlBt1 = new TimelineLite();
      const tlBt2 = new TimelineLite();

      tlBt1.set($circlesTopLeft, { x: 0, y: 0, rotation: -45 });
      tlBt1.add(tl);

      tl2.to($circlesBottomRight, 1.2, { x: 25, y: 25, scaleY: 2, ease: SlowMo.ease.config(0.1, 0.7, false) });
      tl2.to($circlesBottomRight[0], 0.1, { scale: 0.2, x: '-=6', y: '+=3' });
      tl2.to($circlesBottomRight[1], 0.1, { scale: 0.8, x: '+=7', y: '+=3' }, '-=0.1');
      tl2.to($circlesBottomRight[2], 0.1, { scale: 0.2, x: '+=15', y: '-=6' }, '-=0.1');
      tl2.to($circlesBottomRight[0], 1, { scale: 0, x: '+=5', y: '+=15', opacity: 0 });
      tl2.to($circlesBottomRight[1], 1, { scale: 0.4, x: '+=7', y: '+=7', opacity: 0 }, '-=1');
      tl2.to($circlesBottomRight[2], 1, { scale: 0, x: '+=15', y: '-=5', opacity: 0 }, '-=1');

      tlBt2.set($circlesBottomRight, { x: 0, y: 0, rotation: -45 });
      tlBt2.add(tl2);

      tlBt1.timeScale(1 / pm);
      btTl.add(tlBt1);
      btTl.to(bt1.parentNode.querySelectorAll('.button__bg'), 0.8, { scaleY: 1.1 }, 0.1);
      tlBt2.timeScale(1 / pm);
      btTl.add(tlBt2, 0.2);
      btTl.to(bt1.parentNode.querySelectorAll('.button__bg'), 1.8, { scale: 1, ease: 'elastic.out(1.2, 0.4)' }, 1.2 * pm);

      btTl.timeScale(2.6);

      bt1.addEventListener('click', () => {
        bt1c.style.filter = `url(#${this.filterId})`;
        btTl.restart();
      });
    },
  },
  data() {
    return {
      beingDestroyed: false,
    };
  },
}
</script>

<style lang="less">
.svg-filters {
  position: absolute;
  visibility: hidden;
  width: 1px;
  height: 1px;
}

.abstract-button {
  -webkit-font-smoothing: antialiased;
  border: none;
  display: inline-block;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  text-decoration: none;
  user-select: none;
  // letter-spacing: 0.1em;
  color: white;
  padding: 20px 40px;
  transition: background-color 0.1s ease-out;

  &:focus {
    outline: none;
    color: #fff;
  }
}

.distorted-button,
.button__bg {
  .safari & {
    -webkit-filter: none!important;
    filter: none!important;
  }
}

.distorted-button {
  position: relative;
  .abstract-button();
  background: transparent !important; // Otherwise the height wobble effect (done by `.button__container`) would not be looking
  // that good.
  z-index: 0;

  button {
    color: inherit;
  }

  .button__container {
    border-radius: inherit;
    position: absolute;
    display: block;
    width: 200%;
    height: 400%;
    top: -150%;
    left: -50%;
    pointer-events: none;
    z-index: -1;
    transform: translateZ(0);
    transition: all 0.1s ease-out;
  }

  .button__bg {
    @apply bg-primary;
    border-radius: inherit;
    position: absolute;
    .abstract-button();
    width: 50%;
    height: 25%;
    top: 50%;
    left: 25%;
    transform: translateY(-50%);
  }

  .circle {
    position: absolute;
    width: 25px;
    height: 25px;
    border-radius: 15px;
    @apply bg-primary;
    transition: background-color 0.1s ease-out;

    &.top-left {
      top: 40%;
      left: 27%;
    }

    &.bottom-right {
      bottom: 40%;
      right: 27%;
    }
  }
}
</style>
